// frontend/src/features/production/serialization/pages/LotFeedSessionsPage.tsx
//
// ADR-104 Rule SN-04/SN-05 — operator-facing screen for opening/closing
// "which pallet(s) are currently feeding this station" sessions. A station
// can have MORE THAN ONE open session at once (e.g. two battery-plate
// pallets feeding the same line simultaneously) — the list below is not
// artificially limited to one row.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarcodeScanner } from "../components/BarcodeScanner";
import {
  useLotFeedSessionClose,
  useLotFeedSessionList,
  useLotFeedSessionOpen,
} from "../api/serializationApi";
import { useV1ProductionWorkCentersList } from "@/api/generated/v1/v1";
import { useSerializationStore } from "../store/serializationStore";
import { PageBanner } from "@/shared/components/PageBanner";

export function LotFeedSessionsPage() {
  const navigate = useNavigate();
  const { workCenterId, setStation } = useSerializationStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: workCentersData } = useV1ProductionWorkCentersList({ page_size: 1000 } as any);

  const { data: listData, refetch } = useLotFeedSessionList(
    workCenterId ? { work_center: workCenterId, open: true } : undefined
  );
  const { mutate: openSession, isPending: isOpening } = useLotFeedSessionOpen();
  const { mutate: closeSession } = useLotFeedSessionClose();

  const sessions = (listData as any)?.results ?? (listData as any) ?? [];

  const handleScanLot = (lotNumber: string) => {
    if (!workCenterId) {
      setErrorMessage("ابتدا ایستگاه را مشخص کنید.");
      return;
    }
    setErrorMessage(null);
    openSession(
      { data: { work_center_id: workCenterId, lot_number: lotNumber } } as any,
      {
        onSuccess: () => refetch(),
        onError: (err: any) => {
          const detail = err?.response?.data?.detail ?? "باز کردن سشن با خطا مواجه شد.";
          setErrorMessage(String(detail));
        },
      }
    );
  };

  const handleClose = (sessionId: string) => {
    closeSession({ id: sessionId } as any, { onSuccess: () => refetch() });
  };

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }} dir="rtl">
      <PageBanner
        title="پالت‌های فعال این ایستگاه"
        subtitle="مدیریت سشن‌های تغذیه قطعات (باز و بسته کردن جریان پالت به ایستگاه)"
        icon="🏗️"
        helpLink="/help/production/serialization/lot-feed-sessions"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ maxWidth: "640px", margin: "0 auto", padding: "2rem", width: "100%" }}>

        <div style={{ marginBottom: "1.5rem" }}>
          <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem" }}>
            شناسهٔ ایستگاه (WorkCenter)
          </label>
          <select
            value={workCenterId ?? ""}
            onChange={(e) => setStation({ workCenterId: e.target.value || null })}
            dir="ltr"
            style={{ width: "100%", padding: "0.6rem 0.9rem", borderRadius: "8px", background: "#fff", border: "1px solid #ccc" }}
          >
            <option value="">انتخاب ایستگاه کاری (WorkCenter)...</option>
            {workCentersData?.results?.map((wc: any) => (
              <option key={wc.id} value={wc.id}>
                {wc.code} — {wc.name}
              </option>
            ))}
          </select>
        </div>

        <BarcodeScanner
          label="شمارهٔ پالت را اسکن یا تایپ کنید تا سشن باز شود"
          placeholder="شمارهٔ InventoryLot"
          onSubmit={handleScanLot}
          disabled={isOpening || !workCenterId}
        />

        {errorMessage && (
          <div
            role="alert"
            style={{
              marginTop: "1rem",
              padding: "0.75rem 1rem",
              borderRadius: "8px",
              background: "#fef2f2",
              color: "#b91c1c",
              fontSize: "0.85rem",
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={{ marginTop: "1.5rem" }}>
          <h2 style={{ fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.75rem" }}>
            سشن‌های باز ({sessions.length})
          </h2>
          {sessions.length === 0 && (
            <p style={{ fontSize: "0.85rem", color: "#64748b" }}>
              هیچ پالتی روی این ایستگاه باز نیست.
            </p>
          )}
          {sessions.map((session: any) => (
            <div
              key={session.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "0.75rem 1rem",
                borderRadius: "8px",
                background: "#f8fafc",
                marginBottom: "0.5rem",
              }}
            >
              <div>
                <strong dir="ltr">{session.lot_number}</strong>
                <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                  از {new Date(session.started_at).toLocaleString("fa-IR")}
                </div>
              </div>
              <button className="btn btn-secondary" onClick={() => handleClose(session.id)}>
                بستن سشن
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
